import { useContext, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart } from 'recharts';

import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import Dropdown from 'app/components/Dropdown';
import { getSortedPlayers } from 'app/utils/entities/match_data';
import ThemeContext from 'app/common/ThemeContext';
import ExpandableBlock from 'app/components/ExpandableBlock';

import { getSelectedPlayersStatsLines } from './utils';
import PlayersHeadToHeadLine from './PlayersHeadToHeadLine';
import style from './style.module.scss';
import Dota2PlayersHeroesStats from './Dota2PlayersHeroesStats';
import CsPlayersWeaponsStats from './CsPlayersWeaponsStats';

interface Props {
  data: PlayersStatsBlockMatch;
}

const PlayersHeadToHeadBlockGrad: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const [opponent1SelectedPlayerIndex, setOpponent1SelectedPlayerIndex] = useState(0);
  const [opponent2SelectedPlayerIndex, setOpponent2SelectedPlayerIndex] = useState(0);
  const { theme } = useContext(ThemeContext);

  const opponent1 = data.opponents.at(0);
  const opponent2 = data.opponents.at(1);

  const opponent1Players = getSortedPlayers(opponent1).filter(p => !!p);
  const opponent2Players = getSortedPlayers(opponent2).filter(p => !!p);

  const selectedOpponent1Player = opponent1Players.at(opponent1SelectedPlayerIndex);
  const selectedOpponent2Player = opponent2Players.at(opponent2SelectedPlayerIndex);

  const { statsLinesWithChart, statsLinesWithoutChart } = getSelectedPlayersStatsLines(
    selectedOpponent1Player,
    selectedOpponent2Player,
    data.videogame_slug,
    t
  );

  return (
    <BlockGradTemplate
      title={t('blocks-players_head_to_head-title')}
      hasData={data.opponents.some(
        opponent => opponent?.stats && Object.values(opponent.stats.players)?.filter(Boolean).length
      )}
      opponentsLogo={[opponent1, opponent2]}
    >
      <div className={style.playersDropdownContainer}>
        {opponent1Players.length > 0 ? (
          <Dropdown
            options={opponent1Players.map((player, index) => ({
              label: (
                <div className={style.playerLabel}>
                  <span className={style.team1PlayerName}>{player.name}</span>
                  <span className={style.playerRealName}>{`${player.first_name ?? ''} ${
                    player.last_name ?? ''
                  }`}</span>
                </div>
              ),
              value: index.toString()
            }))}
            value={opponent1SelectedPlayerIndex.toString()}
            onChange={value => setOpponent1SelectedPlayerIndex(parseInt(value))}
            className={style.team1PlayerDropdownButton}
            withArrow={false}
            alignment="start"
            arrowIconClassName={style.team1PlayerDropdownArrow}
            optionClassName={style.playerDropdownOption}
          />
        ) : (
          <div />
        )}
        {opponent2Players.length > 0 ? (
          <Dropdown
            options={opponent2Players.map((player, index) => ({
              label: (
                <div className={style.playerLabel}>
                  <span className={style.team2PlayerName}>{player.name}</span>
                  <span className={style.playerRealName}>{`${player.first_name ?? ''} ${
                    player.last_name ?? ''
                  }`}</span>
                </div>
              ),
              value: index.toString()
            }))}
            value={opponent2SelectedPlayerIndex.toString()}
            onChange={value => setOpponent2SelectedPlayerIndex(parseInt(value))}
            className={style.team2PlayerDropdownButton}
            withArrow={false}
            alignment="start"
            arrowIconClassName={style.team2PlayerDropdownArrow}
            optionClassName={style.playerDropdownOption}
          />
        ) : (
          <div />
        )}
      </div>
      <div>
        <ul className={style.linesContainer}>
          {statsLinesWithChart.map(statLine => (
            <PlayersHeadToHeadLine statLine={statLine} key={statLine.name} />
          ))}
        </ul>
        <div className={style.lineChartsContainer}>
          {statsLinesWithChart
            .filter(statLine => (statLine.name as string) !== 'position')
            .map(statLine => (
              <div key={`barchart-${statLine.name}`} className={style.barChartContainer}>
                <BarChart
                  width={49}
                  height={84}
                  data={[
                    {
                      name: statLine.name,
                      team1: statLine.team1Value,
                      team2: statLine.team2Value
                    }
                  ]}
                >
                  <defs>
                    <linearGradient id="team1Fill" x1="0" y1="0" x2="0" y2="100%">
                      <stop
                        offset="-2%"
                        stopColor={theme.design_team_1_gradient_start}
                        stopOpacity={1}
                      />
                      <stop
                        offset="80%"
                        stopColor={theme.design_team_1_gradient_end}
                        stopOpacity={1}
                      />
                    </linearGradient>
                    <linearGradient id="team2Fill" x1="0" y1="0" x2="0" y2="100%">
                      <stop
                        offset="-18%"
                        stopColor={theme.design_team_2_gradient_start}
                        stopOpacity={1}
                      />
                      <stop
                        offset="80%"
                        stopColor={theme.design_team_2_gradient_end}
                        stopOpacity={1}
                      />
                    </linearGradient>
                  </defs>
                  <Bar
                    dataKey="team1"
                    fill="url(#team1Fill)"
                    background={{ fill: theme.design_team_1_background, radius: 4 }}
                    barSize={16}
                    radius={[4, 4, 4, 4]}
                  />
                  <Bar
                    dataKey="team2"
                    fill="url(#team2Fill)"
                    background={{ fill: theme.design_team_2_background, radius: 4 }}
                    barSize={16}
                    radius={[4, 4, 4, 4]}
                  />
                </BarChart>
                <div className={style.barChartName}>
                  {t(`block-players_stats-${statLine.name}`)}
                </div>
              </div>
            ))}
        </div>
        <ul className={style.linesContainer}>
          {statsLinesWithoutChart.map(statLine => (
            <PlayersHeadToHeadLine statLine={statLine} key={statLine.name} />
          ))}
        </ul>
        <ExpandableBlock position="top" arrowSize="medium">
          {(() => {
            switch (data.videogame_slug) {
              case 'dota-2': {
                return (
                  <Dota2PlayersHeroesStats
                    playerTeam1={selectedOpponent1Player as StatPlayerDota2 | undefined}
                    playerTeam2={selectedOpponent2Player as StatPlayerDota2 | undefined}
                  />
                );
              }
              case 'cs-go': {
                return (
                  <CsPlayersWeaponsStats
                    playerTeam1={selectedOpponent1Player as StatPlayerCs | undefined}
                    playerTeam2={selectedOpponent2Player as StatPlayerCs | undefined}
                  />
                );
              }
            }
            return null;
          })()}
        </ExpandableBlock>
      </div>
    </BlockGradTemplate>
  );
};

PlayersHeadToHeadBlockGrad.displayName = 'PlayersHeadToHeadBlockGrad';

export default PlayersHeadToHeadBlockGrad;
